import type { CalibreAliasDetail } from '../../services/firebase';

/**
 * Keeping a calibre's OLD name findable after a rename.
 *
 * References store the calibre's name as text. Rename the calibre and every
 * reference still reading the old name stops matching it by name — unless the
 * old name survives as an alias. It used to survive only on the AI-accept
 * path, and even there a look-alike could suppress it: "Sellita SW200-1" was
 * dropped because "Sellita Sw200-1" already existed as an alias scoped to one
 * brand, and 1,371 references fell out of the calibre's count.
 */

/** Was an existing calibre given a different name? Exact comparison, trimmed. */
export function calibreWasRenamed(storedName: string | undefined | null, formName: string): boolean {
  const before = (storedName ?? '').trim();
  const after = formName.trim();
  return before !== '' && after !== '' && before !== after;
}

/**
 * The alias list to save after a rename: `aliasDetails` plus the old name as a
 * UNIVERSAL alias, unless that exact alias is already there.
 *
 * "Already there" is deliberately strict — the same string, with no brand
 * scope. An alias that differs in case, spacing or punctuation is a different
 * string to every exact-text lookup, and one scoped to a brand does not cover
 * any other brand's references, so neither stands in for the old name.
 *
 * Returns the input array itself when nothing is added.
 */
export function withOldNameAlias(
  aliasDetails: CalibreAliasDetail[],
  storedName: string | undefined | null,
  formName: string,
  keepOldName: boolean,
): CalibreAliasDetail[] {
  if (!keepOldName || !calibreWasRenamed(storedName, formName)) return aliasDetails;
  const oldName = (storedName ?? '').trim();
  const alreadyUniversal = aliasDetails.some(
    (a) => (a.name ?? '').trim() === oldName && !(a.brandId ?? '').trim(),
  );
  if (alreadyUniversal) return aliasDetails;
  return [...aliasDetails, { name: oldName, showInExplore: false }];
}
